import { useCallback, useEffect, useReducer, useRef } from 'react'

import {
  applyAction,
  createRng,
  sudokuEngine,
  toSudokuView,
  type SkillLevels,
  type SudokuAction,
  type SudokuSize,
  type SudokuState,
  type SudokuView,
} from '@hazel/shared'

import { api } from '../api/client.js'
import type { DealtRound } from './useRound.js'
import { useMoveLog } from './useMoveLog.js'
import { useMilestone } from './useMilestone.js'

const SOLO = 'solo'
const PLAYER = { id: SOLO, displayName: 'You', userId: null } as const

interface Round {
  readonly state: SudokuState
}

interface RoundEvent {
  readonly action: SudokuAction
  readonly now: number
}

function start(seed: string, size: SudokuSize, levels: SkillLevels, now: number): Round {
  return {
    state: sudokuEngine.createInitialState(
      { size, levels, startedAt: now },
      [PLAYER],
      createRng(seed),
    ),
  }
}

/** Pure, so React re-running it cannot record a round twice. */
function reduceRound(round: Round, event: RoundEvent): Round {
  const outcome = applyAction(sudokuEngine, round.state, event.action, {
    playerId: SOLO,
    now: event.now,
  })

  // A refused move leaves the round exactly as it was, here and on the server:
  // it is told about the move either way, deals the same grid, and refuses it
  // for the same reason. Which is what makes it safe to send everything rather
  // than to keep a second opinion here about what counted.
  return outcome.ok ? { state: outcome.state } : round
}

export interface Sudoku {
  readonly view: SudokuView
  /** Put a value in a square, or null to rub that one out. */
  readonly place: (cell: number, value: number | null) => void
  /** Rub out everything of hers, leaving the puzzle. */
  readonly clear: () => void
  readonly again: () => void
  readonly finished: boolean
  /** The milestone this round crossed, if it crossed one. Null the rest of the time. */
  readonly milestone: number | null
}

export interface SudokuConfigFromRound {
  readonly size: SudokuSize
  readonly levels: SkillLevels
}

/**
 * Stripey's Sudoku, running the shared engine in the browser.
 *
 * The same shape as Rocky's and Turtley's: the round carries the seed and the
 * levels it was dealt from, so it can only be checked afterwards against what
 * it was actually dealt from. It carries the grid size too, which the others do
 * not — the size is hers to choose and the level is per size, so a round needs
 * both to be dealt again identically.
 *
 * Its levels are its own, loaded and written under the sudoku game key. Filling
 * in a grid and knowing your times tables are unrelated, and one must never
 * move the other.
 */
export function useSudoku(
  round: DealtRound<SudokuConfigFromRound>,
  onFinished: () => void,
): Sudoku {
  const [state, dispatch] = useReducer(reduceRound, round.seed, (seed) =>
    start(seed, round.config.size, round.config.levels, Date.now()),
  )

  const moves = useMoveLog(round.roundId)

  const award = useMilestone()
  const recorded = useRef(false)
  const finished = state.state.status !== 'playing'

  useEffect(() => {
    if (!finished || recorded.current) return
    recorded.current = true

    // Every square she filled, with the moment she filled it, is already at
    // the server.
    void moves
      .flush()
      .then((told) => {
        // Nothing to score if the server never heard the whole round. The
        // round is not spent, so it can still be finished from another device
        // — and asking now would only have it refused.
        if (!told) return undefined

        return api('/api/plays/sudoku', {
          method: 'POST',
          body: JSON.stringify({ roundId: round.roundId }),
        })
      })
      .then(award.notice)
      // A failure to record must not interrupt anything. She filled the grid
      // in either way.
      .catch(() => undefined)
  }, [finished, round.roundId, moves, award.notice])

  const send = useCallback(
    (action: SudokuAction) => {
      const now = Date.now()

      dispatch({ action, now })
      // Never earlier than the move before it: a device whose clock is
      // corrected mid-round would otherwise send times that run backwards.
      moves.record(action, Math.max(0, now - state.state.startedAt))
    },
    [moves, state.state.startedAt],
  )

  return {
    view: toSudokuView(state.state),
    place: useCallback(
      (cell: number, value: number | null) => {
        send({ type: 'place', cell, value })
      },
      [send],
    ),
    /*
     * Sent to the server like any other move, and it has to be.
     *
     * A reset the server never heard of would leave its grid holding squares
     * hers no longer has: every later square lands on a different board, and a
     * grid she abandoned can replay as one she solved. There is a test for
     * exactly that.
     */
    clear: useCallback(() => {
      send({ type: 'clear' })
    }, [send]),
    again: onFinished,
    finished,
    milestone: award.milestone,
  }
}
